import { useState } from 'react';
import type { ProgressEvent } from 'superagent';

import { FileType } from '../../../../../shared/fileUpload';
import { getSignedUrl, triggerBuilds, uploadFile } from '../../../../api/componentUploader';
import { useAddToast } from '../../../../contexts/ToastContext';
import { errorMessageFromResponse, type FailedFetchResponse } from '../../../../helpers/errors';
import { getHumanReadableBytes } from '../../../../utils';
import { getValidFileExtensions } from '../utils';
import type { FileUploadMetadata, Team } from '../../../../types';
import type { FileToUpload, FileToUploadAndStatus } from '../../upload/UploadComponentPage';

const parsePackageNameFromFileName = (fileName: string, fileType: FileType) => {
  const validFileExtensions = getValidFileExtensions(fileType);
  return validFileExtensions.reduce(
    (simplifiedFileName, extension) => simplifiedFileName.split(extension)[0] as string,
    fileName,
  );
};

/**
 * Manages the file upload process
 */
const useFileUpload = (fileType: FileType) => {
  const onAddToast = useAddToast();
  const [file, setFile] = useState<FileToUploadAndStatus | null>(null);
  const size = !file ? 'Size Unknown' : getHumanReadableBytes(file.file.size, 2);

  const onDrop = async (acceptedFiles: FileToUpload[]) => {
    if (!acceptedFiles.length) {
      return;
    }
    const [newFile] = acceptedFiles;
    if (!newFile) {
      return;
    }
    newFile.customName = parsePackageNameFromFileName(newFile.name, fileType);
    const transformedNewFile = {
      file: newFile,
      status: {
        bytesUploaded: 0,
        errorMessage: undefined,
        isSuccessfullyUploaded: false,
      },
    };
    setFile(transformedNewFile);
  };

  const onUpload = async ({
    locator,
    revision,
    revisionType,
    selectedTeams = [],
  }: {
    locator: string;
    revision: string;
    revisionType: 'NEW' | 'REPLACE';
    selectedTeams: Array<Pick<Team, 'id' | 'name' | 'organizationId'>>,
  }) => {
    const packageSpec = locator.split('/')[1];
    if (!file || !packageSpec || !locator) {
      return;
    }
    const { file: uploadingFile } = file;
    try {
      const uploadUrl = await getSignedUrl(packageSpec, revision, fileType);
      await uploadFile(uploadingFile, uploadUrl, (progress: ProgressEvent) => {
        setFile((previousFile) => {
          if (!previousFile) {
            return null;
          }
          return {
            ...previousFile,
            status: {
              bytesUploaded: progress.loaded,
              errorMessage: undefined,
              isSuccessfullyUploaded: false,
            },
          };
        });
      });
      // It is important to rebuild the component if the revision is being replaced
      // or else the revision will not show the results of the new upload
      const shouldForceRebuild = revisionType === 'REPLACE';
      await triggerBuilds(
        {
          archives: [
            {
              packageSpec,
              revision,
              fileType,
              fileName: uploadingFile.name,
            } as FileUploadMetadata,
          ],
          selectedTeams,
          forceRebuild: shouldForceRebuild,
        },
        { fileType },
      );
      setFile((previousFile) => {
        if (!previousFile) {
          return null;
        }
        return {
          ...previousFile,
          status: {
            ...previousFile.status,
            isSuccessfullyUploaded: true,
          },
        };
      });

      // Build appropriate success message based on file type
      let fileTypeName = 'File';
      if (fileType === FileType.SBOM) {
        fileTypeName = 'SBOM';
      } else if (fileType === FileType.Binary) {
        fileTypeName = 'Binary';
      }

      onAddToast({
        content: `New ${fileTypeName} successfully uploaded. Version "${revision}" created`,
        type: 'success',
      });
    } catch (error) {
      const errorMessage = errorMessageFromResponse(error as FailedFetchResponse);
      setFile((previousFile) => {
        if (!previousFile) {
          return null;
        }
        return {
          ...previousFile,
          status: {
            ...previousFile.status,
            errorMessage,
          },
        };
      });
      // Throw error to keep the modal open and display the error to the user
      throw error;
    }
  };

  return {
    resetFile: () => setFile(null),
    file,
    onDrop,
    size,
    onUpload,
    isUploading: !!(file && file?.status.bytesUploaded > 0 && !file?.status.isSuccessfullyUploaded),
  };
};

export default useFileUpload;
